import { Nullable } from '@tcc/common';
import { getLayerUnits } from '../../../config/legendUnits';
import { HARVEST } from '../../../constants/mapLayerIds';
import { LOCALE_GROUPS, HARVEST_YIELD_UNITS } from '../../../constants/units';
import { SILAGE_CORN_CROP_CODE, SUGARCANE_CROP_CODE, COTTON_CROP_CODE } from '../../../constants/cropCodes';

export interface GetCropUnitsParams {
  cropCode: string;
  activeMapLayer: {
    id: string;
  };
  localeGroup: string;
  agVizEnabled: boolean;
  agVizLayerUnits: Nullable<string>
}

const agVizUnitsOverride = {
  '%': 'percent',
  'seeds/ha': 'seed/hectare',
  'kg/ha': 'kilogram/hectare',
};

type LayerOverrideMap = Record<string, Record<string, string>>;
const overrideLocaleGroupForCrops: LayerOverrideMap = {
  [HARVEST.YIELD]: {
    [SILAGE_CORN_CROP_CODE]: LOCALE_GROUPS.YIELD_SILAGE,
    [SUGARCANE_CROP_CODE]: LOCALE_GROUPS.YIELD_SUGARCANE,
    [COTTON_CROP_CODE]: LOCALE_GROUPS.YIELD_COTTON,
  }
}

const hasLocaleGroupOverride = (layerId: string, cropCode: string): boolean => 
  Boolean(overrideLocaleGroupForCrops[layerId] && overrideLocaleGroupForCrops[layerId][cropCode]);

export const getCropUnits = ({
  cropCode,
  activeMapLayer,
  localeGroup,
  agVizEnabled,
  agVizLayerUnits = null,
}: GetCropUnitsParams) => {
  const hasSilageCornCrop = cropCode === SILAGE_CORN_CROP_CODE;
  const hasSugarCaneCrop = cropCode === SUGARCANE_CROP_CODE;

  if (!agVizEnabled && activeMapLayer.id === HARVEST.YIELD && (hasSilageCornCrop || hasSugarCaneCrop)) {
    const newLocaleGroup = cropCode === SILAGE_CORN_CROP_CODE ? LOCALE_GROUPS.YIELD_SILAGE :
      LOCALE_GROUPS.YIELD_SUGARCANE;
    return { key: HARVEST_YIELD_UNITS.METRICTONS_PER_HECTARE, localeGroup: newLocaleGroup };
  }

  if (!agVizEnabled && activeMapLayer.id === HARVEST.YIELD && (cropCode === COTTON_CROP_CODE)) {
    return { key: HARVEST_YIELD_UNITS.METRICTONS_PER_HECTARE, localeGroup: LOCALE_GROUPS.YIELD_COTTON };
  }

  const layerUnitsForCrop = getLayerUnits(activeMapLayer.id, localeGroup);
  if(agVizEnabled && agVizLayerUnits !== null) {
    const overriddenKey = agVizUnitsOverride[agVizLayerUnits] || agVizLayerUnits;

    let overrideLocaleGroup = layerUnitsForCrop?.localeGroup;
    if (hasLocaleGroupOverride(activeMapLayer.id, cropCode)) {
      overrideLocaleGroup = overrideLocaleGroupForCrops[activeMapLayer.id][cropCode];
    }

    return {
      ...layerUnitsForCrop,
      key: overriddenKey,
      localeGroup: overrideLocaleGroup,
    }
  }

  return layerUnitsForCrop;
};